// Client-side upload validation shared by brand / promoter registration and
// event creation. Mirrors the API limits (src/middleware/uploadValidation.js
// in the API repo) so users learn about oversized or unsupported files at
// pick time, before the request is ever sent.

export const MAX_FILE_SIZE_MB = 10;
export const MAX_COMBINED_UPLOAD_MB = 45; // stays under the 50MB server cap

export const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
];
export const DOCUMENT_MIME_TYPES = ['application/pdf'];

export const IMAGE_ACCEPT = IMAGE_MIME_TYPES.join(',');
export const DOCUMENT_ACCEPT = DOCUMENT_MIME_TYPES.join(',');
export const IMAGE_OR_DOCUMENT_ACCEPT = [
  ...DOCUMENT_MIME_TYPES,
  ...IMAGE_MIME_TYPES,
].join(',');

const MB = 1024 * 1024;

export const bytesToMB = (bytes) => Math.round((bytes / MB) * 10) / 10;

const sizeOf = (item) => {
  if (!item) return 0;
  if (typeof item.size === 'number') return item.size; // File | Blob
  return 0;
};

/**
 * Validates freshly picked files. Returns the files that pass plus a list of
 * localized error messages for the ones that don't.
 *
 * @param {File[]} files newly selected files
 * @param {object} opts
 * @param {'image'|'document'|'imageOrDocument'} [opts.kind]
 * @param {number} [opts.existingBytes] size of files already attached to the form
 * @param {Function} opts.t i18next translate fn
 */
export function validateFiles(files, { kind = 'image', existingBytes = 0, t }) {
  const allowedTypes =
    kind === 'document'
      ? DOCUMENT_MIME_TYPES
      : kind === 'imageOrDocument'
        ? [...IMAGE_MIME_TYPES, ...DOCUMENT_MIME_TYPES]
        : IMAGE_MIME_TYPES;
  const accepted = [];
  const errors = [];
  let runningBytes = existingBytes;

  for (const file of files) {
    if (!allowedTypes.includes(file.type)) {
      errors.push(
        kind === 'document'
          ? t('upload.invalid_document_type', {
              defaultValue:
                '"{{name}}" is not a supported document. Please upload a PDF.',
              name: file.name,
            })
          : kind === 'imageOrDocument'
            ? t('upload.invalid_file_type', {
                defaultValue:
                  '"{{name}}" is not a supported file. Please use JPG, PNG, WEBP, GIF, AVIF or PDF.',
                name: file.name,
              })
            : t('upload.invalid_image_type', {
                defaultValue:
                  '"{{name}}" is not a supported image. Please use JPG, PNG, WEBP, GIF or AVIF.',
                name: file.name,
              })
      );
      continue;
    }
    if (file.size > MAX_FILE_SIZE_MB * MB) {
      errors.push(
        t('upload.file_too_large', {
          defaultValue:
            '"{{name}}" is too large ({{size}}MB). The maximum size per file is {{max}}MB.',
          name: file.name,
          size: bytesToMB(file.size),
          max: MAX_FILE_SIZE_MB,
        })
      );
      continue;
    }
    if (runningBytes + file.size > MAX_COMBINED_UPLOAD_MB * MB) {
      errors.push(
        t('upload.combined_too_large', {
          defaultValue:
            'Your files add up to more than {{max}}MB combined. Remove or compress some files before continuing.',
          max: MAX_COMBINED_UPLOAD_MB,
        })
      );
      break;
    }
    runningBytes += file.size;
    accepted.push(file);
  }

  return { accepted, errors };
}

/**
 * Pre-submit guard: checks that every attached File/Blob fits the combined
 * upload budget. Returns a localized error message, or null when fine.
 */
export function combinedUploadError(filesAndBlobs, t) {
  const totalBytes = (filesAndBlobs || [])
    .filter(Boolean)
    .reduce((sum, f) => sum + sizeOf(f), 0);
  if (totalBytes <= MAX_COMBINED_UPLOAD_MB * MB) return null;
  return t('upload.combined_too_large_submit', {
    defaultValue:
      'Your files add up to {{size}}MB, but the maximum combined upload is {{max}}MB. Remove or compress some files and try again.',
    size: bytesToMB(totalBytes),
    max: MAX_COMBINED_UPLOAD_MB,
  });
}

/**
 * Maps a failed submit into an accurate, localized upload error message.
 * Returns null when the failure is not upload-related (caller falls back to
 * its generic error handling).
 */
export function uploadErrorMessage(error, t) {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 413) {
    return (
      data?.error ||
      t('upload.payload_too_large', {
        defaultValue:
          'Your upload is too large. The maximum combined upload is {{max}}MB — remove or compress some files and try again.',
        max: MAX_COMBINED_UPLOAD_MB,
      })
    );
  }
  if (status === 400 && typeof data?.code === 'string' && data.code.startsWith('UPLOAD_')) {
    return data.error || null;
  }
  return null;
}
